import React, { useEffect, useRef, useState } from "react";
import { MoonLoader } from "react-spinners";
import apiUtils, { isApiError } from "../../apiUtils";
import logger from "../../utils/logger";
import { recordRecentPatient } from "../../utils/recentPatients";

export interface PatientSearchResult {
  /** The patient's id (the seeded ids are e.g. "P-123"; legacy Athena ids are numeric). */
  id: string;
  /** The patient's display name. */
  name: string;
  /** The patient's date of birth (may be empty). */
  dob?: string;
}

interface PatientSearchProps {
  onSelectPatient: (patientId: string, encounterId?: string) => void;
}

const SEARCH_ENDPOINT = "/api/home/search";
const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

type SearchStatus = "idle" | "loading" | "loaded" | "error";

/**
 * Patient search by name or id for the standalone cockpit top bar.
 *
 * A query (>= 2 chars) hits `GET /api/home/search?q=` and lists the matches;
 * the backend returns a flat list of `{ id, name, dob }`. Selecting a result —
 * or pressing Enter on a bare id — opens the workspace for that patient (no
 * encounter). Access is gated server-side by `require_patient_access`, so no
 * separate client-side verification round-trip is needed.
 */
const PatientSearch: React.FC<PatientSearchProps> = ({ onSelectPatient }) => {
  const [value, setValue] = useState("");
  const [results, setResults] = useState<PatientSearchResult[]>([]);
  const [status, setStatus] = useState<SearchStatus>("idle");
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  // Monotonic token so a slow older response can't overwrite newer results.
  const requestToken = useRef(0);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }; /* v8 ignore stop */
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  // Debounced search/verify as the query changes.
  useEffect(() => {
    const trimmed = value.trim();
    /* v8 ignore next 4 */
    if (trimmed.length < MIN_QUERY_LENGTH) {
      /* eslint-disable-next-line react-hooks/set-state-in-effect */
      setResults([]);
      setStatus("idle");
      return;
    }

    const handler = setTimeout(async () => {
      const token = ++requestToken.current;
      setStatus("loading");
      setIsOpen(true);

      try {
        const resp = await apiUtils.fetchData<PatientSearchResult[]>(
          `${SEARCH_ENDPOINT}?q=${encodeURIComponent(trimmed)}`,
          "GET",
        );
        /* v8 ignore next */ if (token !== requestToken.current) return; // stale
        if (isApiError(resp)) {
          setStatus("error");
          return;
        }
        const matches = Array.isArray(resp) ? resp : [];
        setResults(matches);
        setHighlight(matches.length > 0 ? 0 : -1);
        setStatus("loaded");
      } catch (err) {
        if (token !== requestToken.current) return;
        logger.devOnly.warn("PatientSearch: lookup failed", {
          error: String(err),
        });
        setStatus("error");
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [value]);

  const handleSelect = (patient: PatientSearchResult) => {
    // Capture the display name so Recent Patients shows the name, not the id.
    recordRecentPatient(patient.id, patient.name);
    onSelectPatient(patient.id);
    setValue("");
    setTimeout(() => setResults([]), 0);
    setIsOpen(false);
    setHighlight(-1);
  }; /* v8 ignore stop */

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = value.trim();
      // A bare numeric query is a patient ID — open it directly without waiting
      // for the debounced search or requiring a click on the dropdown.
      if (/^\d+$/.test(trimmed)) {
        onSelectPatient(trimmed);
        setValue("");
        setTimeout(() => setResults([]), 0);
        setIsOpen(false);
        setHighlight(-1);
        return;
      }
      const chosen = results[highlight] ?? results[0];
      if (chosen) handleSelect(chosen);
      return;
    }
    /* v8 ignore start */ if (!isOpen || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % results.length);
    }
  }; /* v8 ignore stop */

  return (
    <div
      ref={containerRef}
      className="flex-1 max-w-md min-w-[18rem] mx-6 hidden md:block"
    >
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-5 w-5 text-gray-400"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
          placeholder="Search patient by name or ID"
          aria-label="Search patient by name or ID"
          data-testid="patient-search-input"
        />
        {status === "loading" && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <MoonLoader size={16} color="#7a46bb" loading={true} />
          </div>
        )}

        {isOpen && status !== "idle" && (
          <ul className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-auto py-1">
            {status === "error" && (
              <li className="px-4 py-2 text-sm text-red-600">
                Search failed. Please try again.
              </li>
            )}
            {status === "loaded" && results.length === 0 && (
              <li className="px-4 py-2 text-sm text-gray-500">No matches.</li>
            )}
            {results.map((p, index) => (
              <li key={p.id}>
                <button
                  type="button"
                  data-testid="patient-search-result"
                  onClick={() => handleSelect(p)}
                  onMouseEnter={() => setHighlight(index)}
                  className={`w-full text-left px-4 py-2 focus:outline-none ${
                    index === highlight ? "bg-purple-50" : "hover:bg-gray-50"
                  }`}
                >
                  <span className="text-sm font-semibold text-gray-900">
                    {p.name}
                  </span>
                  <span className="block text-xs text-gray-500">
                    DOB: {p.dob || "N/A"} · ID: {p.id}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}; /* v8 ignore stop */

export default PatientSearch;
